/* eslint-disable @typescript-eslint/naming-convention */
import type {
  CasinoServiceParameters,
  CasinoTargeting,
  LayoutCommonObjects,
  QryBlock
} from '@ps/web-martech-cms-types';
import type { RaceQuery } from '@ps/web-shared-api-types/v0/Wmtcdl';
import { identity, pickBy, range, startCase } from 'lodash';
import { from } from 'rxjs';
import type { ValuesType } from 'utility-types';

import { request } from '@swf/core/api/request';
import {
  CasinoJoinOperand,
  CasinoOperand,
  CasinoTargetingMode,
  CasinoTargetingPlatform,
  MoneyOption
} from '@swf/core/enums';
import type { RootState } from '@swf/core/store';
import { dispatch, getState, select } from '@swf/core/store';
import { MarketingOverlayTag, MarketingTag, PlayType } from '@swf/core/types/CasinoGame';
import type {
  CasinoGame,
  CasinoGameInstance,
  CasinoInterruptedGame,
  CasinoTable,
  GameLike,
  LiveCasinoGame
} from '@swf/core/types/CasinoGame';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import { Status } from '@swf/core/types/CasinoRaces';
import type { InstantBonus } from '@swf/core/types/InstantBonus';
import type { StarsCoinReward } from '@swf/core/types/StarsCoinReward';
import { RewardType } from '@swf/core/types/StarsCoinReward';
import { isIPadOS } from '@swf/core/utils';
import { menus } from '@swf/core/utils/casinoFilters';

import ExclusiveSquare from '../assets/casino/exclusive-tag-icnSquare.embed.svg';
import type { CasinoBonusGame, SpinOfDayData } from '../types/CasinoBonus';

interface SearchParams {
  query: string;
  confirmed?: boolean;
  paramCode?: string;
}

// TODO move into model: used in CasinoMyRewardsWidget.tsx and StarsCoinWidget.tsx
export const hasAffordable = ({
  rewards,
  balance
}: {
  rewards: StarsCoinReward[];
  balance: number;
}) => rewards.filter(({ price }) => balance >= price).length;

// TODO move into model: used in casino-my-rewards-widget/components/
export const isActive = (ib: InstantBonus) => ib.status === 10;

// TODO move into model: not used in code base. Possibly need to be removed
const sortIBScore = (bonus: InstantBonus) => (!isActive(bonus) ? 10 : bonus.paused ? 5 : 0);

// TODO move into model: used in CasinoMyRewardsWidget.tsx
export const sortIB = (a: InstantBonus, b: InstantBonus) => sortIBScore(a) - sortIBScore(b);

// TODO move into model: used in CasinoMyRewardsWidget.tsx
export const isInstantBonus = (type?: RewardType) =>
  type && [RewardType.CasinoInstant, RewardType.VegasInstant].includes(type);

export const isFSBType = (type?: RewardType) =>
  type && [RewardType.CasinoFSB, RewardType.VegasFSB].includes(type);

export const isRunning = (status: Status) => [Status.STARTED, Status.REGCLOSED].includes(status);

export const isLiveCasinoGame = (game: CasinoGame | LiveCasinoGame): game is LiveCasinoGame =>
  !!(game as LiveCasinoGame).statusMeta;

export const isCompleted = (status: Status) =>
  [Status.COMPLETED, Status.CANCELLED, Status.ABORTED].includes(status);

export const isLeaderboard = (status: Status) => isRunning(status) || isCompleted(status);

export const isFinished = (status: Status, noStanding: boolean) =>
  [Status.COMPLETED, Status.CANCELLED].includes(status) ||
  (noStanding && status === Status.REGCLOSED);

// TODO move into model: used in CasinoMosaicWidget.tsx
export const buildPayload = ({
  activeFilter,
  pg = 0,
  filters,
  siteName,
  defaultParams
}: {
  activeFilter: number;
  pg?: number;
  filters: string;
  siteName: string;
  defaultParams: CasinoServiceParameters;
}) => {
  const filterType = menus[filters] || {};
  const filter = filterType[activeFilter];
  let params: CasinoServiceParameters = { ...defaultParams };
  if (filter) {
    params = filter.makeQuery(params, siteName);
  }
  return { meta: { size: params.psz }, params: { ...params, pg } };
};

export const gamesPerColumn = (mosaic: string) => {
  switch (mosaic) {
    case '4 squares':
      return 4;
    case '1 top 1 bottom':
      return 2;
    case '1 big square':
      return 1;
    default:
      return 3;
  }
};

export const mosaicIndexRange: number[] = range(1, 4);

export const getWindow = ({
  table,
  playType = PlayType.Single,
  isMobile,
  isEmbedded
}: {
  table: CasinoTable | CasinoInterruptedGame | CasinoGameInstance | CasinoBonusGame;
  playType?: PlayType;
  isMobile: boolean;
  isEmbedded: boolean;
}): Window | null => {
  if (isEmbedded) {
    return null;
  } else if (isMobile || isIPadOS) {
    // to handle desktop mode on iPadOS
    return window;
  }
  // eslint-disable-next-line stars-web/no-direct-browser-globals-access
  return window.open(
    '',
    `${
      (table as CasinoInterruptedGame).tId ||
      (table as CasinoTable).id ||
      (table as CasinoBonusGame).bonusId
    }-${playType}`,
    'width=960,height=540,resizable=1'
  );
};

export const isRedirected = (gameWindow: Window) => {
  try {
    return gameWindow.location.href !== 'about:blank';
  } catch {
    return true;
  }
};

export const closeWindow = (gameWindow: Window | null | undefined, isMobile: boolean) => {
  if (gameWindow && !isMobile) {
    gameWindow.close();
  }
};

export const defaultQuery: QryBlock[] = [
  {
    formalparamcode: 'tagname',
    values: ['Slots'],
    operand: CasinoOperand.EQ,
    join: CasinoJoinOperand.OR,
    dynamic: false,
    exclude_query_values: []
  }
];

export const isGameInfoAvailable = (game: CasinoGame) => game.tags?.includes(MarketingTag.GameInfo);

export const isGameInfoConfigured = (params?: { include_tags: string[] }) =>
  params?.include_tags?.includes(MarketingTag.GameInfo);

export const isLive = (icon: string) => 'icnLive' === icon;

export const tilesPerRowFromIcon = (
  icon: ValuesType<CasinoServiceParameters['ims']['icon_tags']>
) => {
  switch (icon) {
    case 'icnHeroWide':
      return 1;
    case 'icnHero':
    case 'icnWide':
    case 'icnLive':
    case 'icnGameTrailerIcon':
    case 'icnGameTrailerVideo':
      return 3;
    default:
      return 6;
  }
};

export const searchRequest = async (
  params: Pick<SearchParams, 'paramCode' | 'query'> | QryBlock[],
  rootState: RootState
): Promise<CasinoGame[]> => {
  const siteName = select.cms.getSiteName(rootState);
  // TODO: check if still required and remove
  const moneyOption: QryBlock = {
    formalparamcode: 'moneyoption',
    values: [`${select.currency.isPlayMoney(rootState) ? MoneyOption.Play : MoneyOption.Real}`],
    operand: CasinoOperand.EQ,
    join: CasinoJoinOperand.AND,
    dynamic: false,
    exclude_query_values: []
  };
  let qry: QryBlock[];
  if (Array.isArray(params)) {
    qry = [...params, moneyOption];
  } else {
    qry = [
      {
        formalparamcode: (params.paramCode as QryBlock['formalparamcode']) || 'search_text',
        values: [params.query],
        operand: CasinoOperand.EQ,
        join: CasinoJoinOperand.AND,
        dynamic: false,
        exclude_query_values: []
      },
      moneyOption
    ];
  }

  const { body } = await request({
    service: 'casino.games.all',
    ...select.cms.getRequestOptions(rootState),
    body: {
      pg: 0,
      qry,
      order: [{ column: 'numOf', sort: 1 }],
      grp: true,
      ims: { tags: ['icnSquare'] },
      psz: 30,
      tgs: true,
      includeTags: [`Exclusive ${siteName}`, `New ${siteName}`, `Hot ${siteName}`, `High Roller`]
    }
  });
  return body;
};

export const search = (query: QryBlock[], rootState: RootState) =>
  from(searchRequest(query, rootState));

// TODO move into model: not used in code base. Possibly need to be removed
export const lauchRaceGameWithId = async (
  raceId: string,
  game: CasinoGame,
  externalLaunch?: boolean
) => {
  const query = {
    status: 'STARTED',
    imageTags: ['icnSquare'],
    size: 10,
    raceId
  } as RaceQuery;
  const rootState = getState();
  try {
    const { body } = await request({
      service: 'casino.races.all.stream',
      ...select.cms.getRequestOptions(rootState),
      body: {
        raceQuery: [query]
      }
    });
    const races = body.races as CasinoRace[];
    if (races.length > 0) {
      dispatch.casino.race({
        game,
        raceId: races[0].id,
        raceTypes: races[0].raceTypes,
        tncVersion: races[0].tncVersion,
        externalLaunch
      });
    }
  } catch {
    // do nothing
  }
};

export const launchGame = ({
  id = '',
  code,
  raceId,
  race,
  fsb,
  externalLaunch
}: {
  id?: string;
  code?: string;
  raceId?: string;
  race?: boolean;
  fsb?: string;
  externalLaunch?: boolean;
}) => {
  const fsbLaunch = !!fsb;
  const join: Omit<QryBlock, 'formalparamcode' | 'values'> = {
    operand: CasinoOperand.EQ,
    join: CasinoJoinOperand.AND,
    dynamic: false,
    exclude_query_values: []
  };
  let gameQuery: QryBlock[] = [];

  if (id) {
    gameQuery = [{ values: [startCase(id)], formalparamcode: 'search_text', ...join }];
  }

  if (code) {
    const [gameType, variantType] = code.split('-');
    gameQuery = [
      { values: [gameType], formalparamcode: 'gametype', ...join },
      { values: [variantType], formalparamcode: 'varianttype', ...join }
    ];
  }
  search(gameQuery, getState()).subscribe(([game]) => {
    const table = game?.tables[0];
    if ((race || raceId) && table) {
      lauchRaceGameWithId(raceId || id, game, externalLaunch);
    } else if (table) {
      dispatch.casino.launch({ game, table, fsbLaunch, externalLaunch });
    }
  });
};

export const isVisibleByAuth = (
  loggedInOnly = false,
  hideOnLogin = false,
  isAuthenticated = false
): boolean =>
  (loggedInOnly && isAuthenticated && !hideOnLogin) ||
  (!loggedInOnly && ((hideOnLogin && !isAuthenticated) || !hideOnLogin));

type ABTestMatch = (targeting: Required<CasinoTargeting>, rootState: RootState) => boolean;

const abTests: ABTestMatch[] = [
  ({ users }, rootState) => {
    const userId = select.account.getUserId(rootState);
    return (!!userId && users.includes(userId)) || !users.length;
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention, stars-web/no-direct-browser-globals-access
  ({ user_agent }) => !!window.navigator.userAgent.match(new RegExp(user_agent)),
  ({ platform }, rootState) => {
    const isMobile = select.device.isMobile(rootState);
    return (
      platform === CasinoTargetingPlatform.All ||
      (isMobile && platform === CasinoTargetingPlatform.Mobile) ||
      (!isMobile && platform === CasinoTargetingPlatform.Desktop)
    );
  }
];

export const isVisibleByAB = (rootState: RootState, targeting?: CasinoTargeting): boolean => {
  let visible = true;
  const {
    mode,
    users = [],
    platform = CasinoTargetingPlatform.All,
    user_agent = ''
  } = targeting || {};

  if (mode && mode !== CasinoTargetingMode.All) {
    const match = abTests.reduce(
      (memo, current) => memo && current({ mode, users, platform, user_agent }, rootState),
      true
    );

    visible = mode === CasinoTargetingMode.Exclude ? !match : match;
  }
  return visible;
};

export const isGame = (test: GameLike): test is CasinoGame =>
  (test as CasinoGame).gametype !== undefined;

export const ensureAbsolute = (path: string): string =>
  (path?.includes && path.includes('//')) || typeof window === 'undefined'
    ? path
    : // eslint-disable-next-line stars-web/no-direct-browser-globals-access
      window.location.origin + path;

const MarketingOverlayTagImages = {
  [MarketingOverlayTag.Exclusive]: ensureAbsolute(ExclusiveSquare)
};

export const marketingOverlays = (
  tags: string[] = Object.values(MarketingOverlayTag),
  invert = false
) => tags.filter((tag) => (invert && !tag.startsWith('o_')) || (!invert && tag.startsWith('o_')));

export const getUGCImages = (game?: CasinoGame) => {
  const [tag] = marketingOverlays(game?.tags);
  const [cleanTag] = tag ? tag.split(' ') : [];

  return pickBy(
    {
      background: game ? game.images.icnSquare : undefined,
      overlay: cleanTag
        ? MarketingOverlayTagImages[cleanTag as keyof typeof MarketingOverlayTagImages]
        : undefined
    },
    (value) => typeof value !== 'undefined'
  );
};

export const marketingOverlaySiteName = (siteName: string) =>
  marketingOverlays().map((v) => `${v} ${siteName}`);

export const marketingMultipliers = Object.values(MarketingTag).filter((tag) =>
  tag.startsWith('multiplier')
);

export const marketingMultipliersSiteName = (siteName: string) =>
  marketingMultipliers.map((v) => `${v} ${siteName}`);

export const marketingTagsWithSite = Object.values({ ...MarketingTag, ...MarketingOverlayTag }).map(
  (value) => {
    switch (value) {
      case MarketingTag.HighRoller:
      case MarketingTag.GameInfo:
      case MarketingTag.Slots:
      case MarketingTag.LiveLabel:
        return value;
      default:
        return `${value} [SITE]`;
    }
  }
);

export const getStyle = (layout: LayoutCommonObjects) => {
  if (!layout) return undefined; // TODO: remove post migration as layout should always be defined

  const { padding_bottom: paddingBottom, padding_top: paddingTop } = layout;
  return {
    paddingTop: paddingTop || undefined,
    paddingBottom: paddingBottom || undefined
  };
};

export const sortSOTDData = (g1: SpinOfDayData, g2: SpinOfDayData): number => {
  if (g1.expiryTime === g2.expiryTime) {
    if (g1.numberOfSpins === g2.numberOfSpins) {
      return g1.bonusId > g2.bonusId ? 1 : -1;
    } else {
      return g1.numberOfSpins < g2.numberOfSpins ? 1 : -1;
    }
  }
  return g1.expiryTime > g2.expiryTime ? 1 : -1;
};

export const getTablePropsFromGame = (game: CasinoGame): CasinoTable => ({
  ...game.tables[0],
  minBet: game.gameMinBet,
  maxBet: game.gameMaxBet,
  featureNames: game.featureNames
});
